import { FEATURE_FLAGS } from "./featureFlag.js";

export const APP_USAGE_SOURCE_TYPES = {
  PER_APP_USER: "perAppUser",
  RATE: "rate",
};

// Source descriptor types:
//   perAppUser: ratio = numerator / activeAppUsers
//   rate:       ratio = numerator / denominator
//
// `numerator`/`denominator` are logical keys that the API service maps
// to concrete fetchers (PostHog endpoint, repo method, etc).

// if you add a new metrics, check that sum of default weights is still 100
export const APP_USAGE_CATEGORIES = [
  {
    key: "agenda",
    label: "Agenda",
    featureFlag: null,
    metrics: [
      {
        key: "agenda_open",
        label: "Agenda open",
        weight: 15,
        source: { type: "perAppUser", numerator: "agendaOpens" },
      },
    ],
  },
  {
    key: "sessions",
    label: "Sessions",
    featureFlag: null,
    metrics: [
      {
        key: "session_open",
        label: "Session open",
        weight: 10,
        source: { type: "perAppUser", numerator: "sessionOpens" },
      },
      {
        key: "session_bookmark",
        label: "Session bookmark",
        weight: 5,
        source: { type: "perAppUser", numerator: "sessionBookmarkers" },
      },
    ],
  },
  {
    key: "checkin",
    label: "Check-in",
    featureFlag: null,
    metrics: [
      {
        key: "checkin",
        label: "Attendee check-in",
        weight: 12,
        source: {
          type: "rate",
          numerator: "checkedInTickets",
          denominator: "totalTickets",
        },
      },
    ],
  },
  {
    key: "registration",
    label: "Registration",
    featureFlag: FEATURE_FLAGS.REGISTRATIONS,
    metrics: [
      {
        key: "registration_completed",
        label: "Completed registration",
        weight: 8,
        source: {
          type: "rate",
          numerator: "completedRegistrations",
          denominator: "startedRegistrations",
        },
      },
    ],
  },
  {
    key: "sponsors",
    label: "Sponsors",
    featureFlag: null,
    metrics: [
      {
        key: "sponsor_open",
        label: "Sponsor profile open",
        weight: 8,
        source: { type: "perAppUser", numerator: "sponsorOpens" },
      },
    ],
  },
  {
    key: "pages",
    label: "Pages",
    featureFlag: null,
    metrics: [
      {
        key: "page_open",
        label: "Info page open",
        weight: 7,
        source: { type: "perAppUser", numerator: "pageOpens" },
      },
    ],
  },
  {
    key: "polls",
    label: "Polls",
    featureFlag: FEATURE_FLAGS.POLLS,
    metrics: [
      {
        key: "poll_answer",
        label: "Poll answer",
        weight: 7,
        source: { type: "perAppUser", numerator: "pollAnswerers" },
      },
    ],
  },
  {
    key: "surveys",
    label: "Surveys",
    featureFlag: FEATURE_FLAGS.SURVEYS,
    metrics: [
      {
        key: "survey_start",
        label: "Answered at least one question",
        weight: 4,
        source: { type: "perAppUser", numerator: "surveyStarters" },
      },
      {
        key: "survey_complete",
        label: "Completed survey",
        weight: 3,
        source: { type: "perAppUser", numerator: "surveyCompleters" },
      },
    ],
  },
  {
    key: "speakers",
    label: "Speakers",
    featureFlag: null,
    metrics: [
      {
        key: "speaker_open",
        label: "Speaker profile open",
        weight: 5,
        source: { type: "perAppUser", numerator: "speakerOpens" },
      },
    ],
  },
  {
    key: "giveaway",
    label: "Giveaway",
    featureFlag: FEATURE_FLAGS.GIVEAWAYS,
    metrics: [
      {
        key: "giveaway_enter",
        label: "Giveaway entry",
        weight: 3,
        source: { type: "perAppUser", numerator: "giveawayEntrants" },
      },
      {
        key: "giveaway_complete",
        label: "Completed giveaway",
        weight: 2,
        source: { type: "perAppUser", numerator: "giveawayCompleters" },
      },
    ],
  },
  {
    key: "session_ratings",
    label: "Session ratings",
    featureFlag: FEATURE_FLAGS.SESSION_RATINGS,
    metrics: [
      {
        key: "session_rating",
        label: "Session rating",
        weight: 4,
        source: { type: "perAppUser", numerator: "sessionRaters" },
      },
    ],
  },
  {
    key: "tags",
    label: "Tags (profile)",
    featureFlag: FEATURE_FLAGS.ATTENDEE_INTERESTS_TAGS,
    metrics: [
      {
        key: "people_tag",
        label: "At least one people tag",
        weight: 3,
        source: { type: "perAppUser", numerator: "usersWithTag" },
      },
    ],
  },
  {
    key: "email",
    label: "Email",
    featureFlag: null,
    metrics: [
      {
        key: "email_open",
        label: "Email open",
        weight: 2,
        source: {
          type: "rate",
          numerator: "emailsOpened",
          denominator: "emailsDelivered",
        },
      },
    ],
  },
  {
    key: "push",
    label: "Push",
    featureFlag: null,
    metrics: [
      {
        key: "push_open",
        label: "Push notification open",
        weight: 2,
        source: {
          type: "rate",
          numerator: "pushOpened",
          denominator: "pushDelivered",
        },
      },
    ],
  },
];

export const APP_USAGE_METRIC_KEYS = APP_USAGE_CATEGORIES.flatMap((c) =>
  c.metrics.map((m) => m.key)
);

export const APP_USAGE_DEFAULT_WEIGHTS = Object.fromEntries(
  APP_USAGE_CATEGORIES.flatMap((c) => c.metrics.map((m) => [m.key, m.weight]))
);

// Tolerance for the sum-of-weights check. Summing 17 multiples of 0.1 in
// IEEE-754 can drift by ~1e-12, so anything below 0.01 is safe headroom
// without letting real misconfiguration (e.g. a saved 99 or 101) through.
export const APP_USAGE_WEIGHTS_SUM_TOLERANCE = 0.01;

const weightSchema = {
  type: "number",
  minimum: 0,
  maximum: 100,
};

export const appUsageWeightsBodySchema = {
  type: "object",
  additionalProperties: false,
  required: APP_USAGE_METRIC_KEYS,
  properties: Object.fromEntries(
    APP_USAGE_METRIC_KEYS.map((k) => [k, weightSchema])
  ),
};

export const getAppUsageWeightsSchema = {
  params: {
    type: "object",
    properties: { eventId: { type: "number" } },
    required: ["eventId"],
  },
};

export const updateAppUsageWeightsSchema = {
  params: {
    type: "object",
    properties: { eventId: { type: "number" } },
    required: ["eventId"],
  },
  body: appUsageWeightsBodySchema,
};

export const getAppUsageSchema = {
  params: {
    type: "object",
    properties: { eventId: { type: "number" } },
    required: ["eventId"],
  },
  querystring: {
    type: "object",
    properties: {
      timeFrame: {
        type: "string",
        enum: ["event", "app", "custom"],
        default: "event",
      },
      dateFrom: { type: "string", format: "date" },
      dateTo: { type: "string", format: "date" },
    },
    additionalProperties: false,
  },
};

const roundOneDecimal = (n) => Math.round(n * 10) / 10;

// Merge user overrides with defaults (drops unknown keys, fills missing).
// If sum diverges from 100, renormalize proportionally to keep math sane
// even when a new metric is added to the registry after a save.
export const resolveAppUsageWeights = (overrides) => {
  const merged = { ...APP_USAGE_DEFAULT_WEIGHTS };
  if (overrides && typeof overrides === "object") {
    for (const key of APP_USAGE_METRIC_KEYS) {
      const v = overrides[key];
      if (typeof v === "number" && Number.isFinite(v) && v >= 0) {
        merged[key] = v;
      }
    }
  }
  const sum = Object.values(merged).reduce((a, b) => a + b, 0);
  if (sum === 0) return merged;
  if (Math.abs(sum - 100) <= APP_USAGE_WEIGHTS_SUM_TOLERANCE) return merged;
  const factor = 100 / sum;
  return Object.fromEntries(
    Object.entries(merged).map(([k, v]) => [k, roundOneDecimal(v * factor)])
  );
};

// Pull the metric's raw numerator/denominator from inputs.
const prepareMetric = (metric, resolved, metricInputs, users) => {
  const inp = metricInputs?.[metric.key] ?? {};
  return {
    def: metric,
    weightConfigured: resolved[metric.key] ?? 0,
    numerator: Number(inp.numerator) || 0,
    denominator:
      metric.source.type === "perAppUser"
        ? users
        : Number(inp.denominator) || 0,
  };
};

// Prepare metrics and mark category included (feature flag on / not gated).
// Zero-data metrics stay included and just contribute 0 to the score —
// only a disabled feature flag removes the category from the pool.
const prepareCategory = (category, resolved, metricInputs, flags, users) => {
  const included =
    category.featureFlag == null || flags.has(category.featureFlag);
  const metrics = category.metrics.map((m) =>
    prepareMetric(m, resolved, metricInputs, users)
  );
  return {
    def: category,
    metrics,
    weightConfigured: roundOneDecimal(
      metrics.reduce((a, m) => a + m.weightConfigured, 0)
    ),
    included,
  };
};

// Apply redistribution factor; compute ratio and contribution to score.
const finalizeMetric = (m, factor, included) => {
  const weightEffective = included
    ? roundOneDecimal(m.weightConfigured * factor)
    : 0;
  const ratio =
    m.denominator > 0 ? Math.min(1, m.numerator / m.denominator) : 0;
  return {
    key: m.def.key,
    label: m.def.label,
    weightConfigured: m.weightConfigured,
    weightEffective,
    numerator: m.numerator,
    denominator: m.denominator,
    ratio,
    contribution: Math.round(weightEffective * ratio * 100) / 100,
  };
};

// Finalize all metrics and roll up to category score + effective weight.
const finalizeCategory = (c, factor) => {
  const metrics = c.metrics.map((m) => finalizeMetric(m, factor, c.included));
  return {
    key: c.def.key,
    label: c.def.label,
    featureFlag: c.def.featureFlag,
    included: c.included,
    weightConfigured: c.weightConfigured,
    weightEffective: c.included
      ? roundOneDecimal(c.weightConfigured * factor)
      : 0,
    score:
      Math.round(metrics.reduce((a, m) => a + m.contribution, 0) * 100) / 100,
    metrics,
  };
};

// Prepare inputs, redistribute weight from excluded categories, finalize.
//   score = Σ (ratio × weightEffective) — clamped 0–100.
export const computeAppUsage = ({
  weights,
  metricInputs,
  activeAppUsers,
  enabledFeatureFlags,
}) => {
  const resolved = resolveAppUsageWeights(weights);
  const flags = new Set(enabledFeatureFlags ?? []);
  const users = activeAppUsers ?? 0;

  const prepared = APP_USAGE_CATEGORIES.map((c) =>
    prepareCategory(c, resolved, metricInputs, flags, users)
  );

  const includedSum = prepared
    .filter((c) => c.included)
    .reduce((a, c) => a + c.weightConfigured, 0);
  const factor = includedSum === 0 ? 0 : 100 / includedSum;

  const categories = prepared.map((c) => finalizeCategory(c, factor));
  const totalScore = Math.min(
    100,
    Math.max(0, Math.round(categories.reduce((a, c) => a + c.score, 0)))
  );

  return { score: totalScore, activeAppUsers: users, categories };
};
